import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Constants } from '../../core/constants';
import { BroadcastService } from '../../services/broadcast.service';
import { Toro } from '../common/enumerations';
import { AuxiliaryEditModel } from '../models/auxiliary-edit.model';
import { Auxiliary } from '../models/auxiliary.model';
import { FireAndWaitForNotificationCommandResponse } from '../models/command-responses/fire-and-wait-for-notification-command-response.model';
import { WaitUntilCompleteCommandResponse } from '../models/command-responses/wait-until-complete-command-response.model';
import { ControllerStatus } from '../models/controller-status.model';
import { ImageAsset } from '../models/image-asset.model';
import { ControllerInfoForConfigDisplay } from '../models/irrigation/ControllerInfoForConfigDisplay.model';
import { ControllerProgrammability } from '../models/programmability-ux/controller-programmability.model';
import { AddControllerModel } from '../models/toro-add-controller.model';
import { ToroControllerBundle } from '../models/toro-controller-bundle';
import { ToroControllerConfiguration } from '../models/toro-controller-configuration.model';
import { ControllerDefinition } from '../models/toro-controller-definition.model';
import { ToroController } from '../models/toro-controller.model';
import { EditControllerModel } from '../models/toro-edit-controller.model';
import { ToroEditScheduleModel } from '../models/toro-edit-schedule.model';
import { ToroIrrigationCrudResponseModel } from '../models/toro-irrigation-crud-response.model';
import { ToroProgramStation } from '../models/toro-program-station.model';
import { ToroProgram } from '../models/toro-program.model';
import { ToroStationEdit } from '../models/toro-station-edit.model';
import { ToroStation } from '../models/toro-station.model';
import { ControllerApiService } from '../services/controller-api.service';
import { ProgramApiService } from '../services/program-api.service';
import { StationApiService } from '../services/station-api.service';
import { selectZone } from './../../irrigation/store/controllers/controller.actions';
import { ControllerState } from './../../irrigation/store/controllers/controller.entity';
import { getCurrentController, getCurrentStation } from './../../irrigation/store/irrigation.selectors';
import { NotificationManagerService } from './notification-manager.service';
import RunningMode = Toro.RunningMode;

@Injectable()
export class ControllerManagerService implements OnDestroy {
    private _controllerBundles: { [locationId: number]: ToroControllerBundle[] } = {};
    private _currentController: ToroController;
    private _currentZone: ToroStation;
    private _controllerDefinitions: ControllerDefinition[];

    // =========================================================================================================================================================
    // C'tor and Destroy
    // =========================================================================================================================================================

    constructor(private controllerApiService: ControllerApiService,
        private programApiService: ProgramApiService,
        private stationApiService: StationApiService,
        private broadcastService: BroadcastService,
        private controllerStore: Store<ControllerState>,
        private notificationManager:NotificationManagerService) {

        this.broadcastService.userSignedOut
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.clearCache();
            });

        this.controllerStore.select(getCurrentController).subscribe(controller => {
            this.currentController = controller;
        });

        this.controllerStore.select(getCurrentStation).subscribe(station => {
            this._currentZone = station;
        });


        // pre-load for quicker UI responsiveness
        // this.getControllerDefinitions().pipe(take(1)).subscribe();
    }

    ngOnDestroy() {
        /* Implemented to support untilDestroyed */
    }

    // =========================================================================================================================================================
    // Pubic Methods
    // =========================================================================================================================================================

    clearCache() {
        Object.keys(this._controllerBundles).forEach(key => {
            this._controllerBundles[key].length = 0;
        });
        this._controllerBundles = {};
    }

    get currentZone(): ToroStation {
        if (!this._currentZone) {
            return null;
        }
        return this._currentZone;
    }

    set currentZone(zone: ToroStation) {
        this._currentZone = zone;
        this.controllerStore.dispatch(selectZone({ currentStation: zone }));
    }

    get currentController(): ToroController {
        if (!this._currentController) {
            return null;
        }
        return this._currentController;
    }
    set currentController(controller: ToroController) {
        this._currentController = controller;
    }

    /* get currentControllerBundles(): { [locationId: number]: ToroControllerBundle[] } {
        if (!this._controllerBundles) {
            return null;
        }
        return this._controllerBundles;
    } */


    updateControllerImageAssetInCache(locationId: number, controllerId: number, imageAsset: ImageAsset) {
        const controllerToUpdate = this._controllerBundles[locationId].find(controllerBundle => controllerBundle.Controller.Id === controllerId);
        controllerToUpdate.imageAsset = imageAsset;
    }

    updateZoneImageAssetOnCache(locationId: number, controllerId: number, stationNumber: number, imageAsset: ImageAsset) {
        const controllerToUpdate = this._controllerBundles[locationId].find(controllerBundle => controllerBundle.Controller.Id === controllerId);
        const stationToUpdate = controllerToUpdate.Configuration.Stations.find(station => station.StationNumber === stationNumber);

        stationToUpdate.ImageAsset = imageAsset;
    }

    public getImageForController(deviceType: string) {
        switch (deviceType) {
            case 'Evolution':
                return Constants.imageBasePath + 'dd_Controller_EVO.png';
            case 'Rain Dial':
                return Constants.imageBasePath + 'dd_Controller_RD.png';
            case 'KD II':
            case 'KD2':
                return Constants.imageBasePath + 'dd_Controller_KD2.png';
            case 'Total Control':
                return Constants.imageBasePath + 'dd_Controller_TC.png';
            case 'Kwik Dial':
                return Constants.imageBasePath + 'dd_Controller_KD.png';
            case 'TMC-424':
                return Constants.imageBasePath + 'dd_Controller_TMC424.png';
            case 'TMC-212':
                return Constants.imageBasePath + 'dd_Controller_TMC212.png';
            case 'MC-E':
                return Constants.imageBasePath + 'dd_Controller_MCE.png';
            case 'Custom Command':
                return Constants.imageBasePath + 'dd_Controller_CC.png';
            case 'Evolution Ag':
                return Constants.imageBasePath + 'dd_Controller_EVO_Ag.png';
            case 'LXC':
            case 'LXCEXP':                                 //TODO LXC
                return Constants.imageBasePath + 'dd_Controller_LXC.png';
        }
    }

    public getControllerDetails(locationId: number, controllerId: number, cached: boolean = true): Observable<ToroControllerBundle> {
        if (cached && this._controllerBundles && locationId in this._controllerBundles) {
            const controllerBundle = this._controllerBundles[locationId].find(controller => controller.Controller.Id === controllerId);

            if (controllerBundle) {
                return of(controllerBundle);
            }
        }

        return this.controllerApiService.getControllerDetails(locationId, controllerId);
    }

    public getControllersForLocation(locationId: number, cached: boolean = true): Observable<ToroControllerBundle[]> {
        // Return cached data if it exists and has been requested.
        if (cached && this._controllerBundles && locationId in this._controllerBundles) {
            return of(this._controllerBundles[locationId]);
        }

        return this.controllerApiService.getControllersForLocation(locationId)
            // return this.controllerApiService.getControllersListForLocation(locationId)
            .pipe(
                map((controllerBundles: ToroControllerBundle[]) => {
                    if (this._controllerBundles && locationId in this._controllerBundles) {
                        if (this._controllerBundles[locationId]) {
                            this._controllerBundles[locationId] = [];
                        }
                    }
                    this._controllerBundles[locationId] = [];
                    if ((controllerBundles && controllerBundles !== null) && controllerBundles.length > 0)
                        this._controllerBundles[locationId].push(...controllerBundles);

                    return controllerBundles;
                })
            );
    }

    public getControllersForLocationByGatewayId(gatewayId: number, locationId: number): Observable<ToroControllerBundle[]> {
        // Return cached data if it exists and has been requested.

        // return this.controllerApiService.getControllersForLocation(locationId)
        return this.controllerApiService.getControllersListForLocation(gatewayId, locationId)
            .pipe(
                map((controllerBundles: ToroControllerBundle[]) => {
                    if (this._controllerBundles && locationId in this._controllerBundles) {
                        if (this._controllerBundles[locationId]) {
                            this._controllerBundles[locationId] = [];
                        }
                    }
                    this._controllerBundles[locationId] = [];
                    if ((controllerBundles && controllerBundles !== null) && controllerBundles.length > 0)
                        this._controllerBundles[locationId].push(...controllerBundles);

                    return controllerBundles;
                })
            );
    }

    public getProgramStations(program: ToroProgram, controllerBundle: ToroControllerBundle): ToroProgramStation[] {
        const programStations: ToroProgramStation[] = [];
        const stations = controllerBundle.Configuration.Stations;
        const stationRuntimes = program.ExtendedInfo.StationRuntimes;

        for (let i = 0; i < stationRuntimes.length; i++) {
            if (stationRuntimes[i] > 0) {
                const ps = stations.find(station => station.StationNumber === i + 1) as ToroProgramStation;
                ps.RuntimeInSeconds = stationRuntimes[i];
                programStations.push(ps);
            }
        }

        return programStations;
    }

    public startStation(controllerId: number, stationNumber: number, duration: number) {
        return this.controllerApiService.startStation(controllerId, stationNumber, duration);
    }

    public startProgram(controllerId: number, programNumber: number) {
        return this.controllerApiService.startProgram(controllerId, programNumber);
    }

    public startAuxiliary(controllerId: number, auxiliaryNumber: number) {
        return this.controllerApiService.startAuxiliary(controllerId, auxiliaryNumber);
    }

    public stopControllerIrrigation(controllerId: number): Observable<boolean> {
        return this.controllerApiService.stopControllerIrrigation(controllerId);
    }

    public stopStationIrrigation(controllerId: number, stationNumber:number): Observable<boolean> {
        return this.controllerApiService.stopStationIrrigation(controllerId, stationNumber);
    }

    public stopProgram(controllerId: number, ProgramNumber:number): Observable<boolean> {
        return this.controllerApiService.stopProgramIrrigation(controllerId, ProgramNumber);
    }

    public getControllerStatus(controllerId: number): Observable<ControllerStatus> {
        return this.controllerApiService.getControllerStatus(controllerId);
    }

    /**
     *  Method that requests running status from controller. If successful, will return an IrrigationControllerStatusChangedNotification for the given
     *  controller
     */
    public getControllerRunningStatus(controllerId: number): Observable<{ controllerId: number, sentSuccessfully: boolean }> {
        return this.controllerApiService.getControllerRunningStatus(controllerId);
    }

    public setRunningMode(controllerId: number, runningMode: RunningMode, daysToRemainOff: number) {
        return this.controllerApiService.setRunningMode(controllerId, runningMode, daysToRemainOff);
    }

    public getControllerDefinitions(): Observable<ControllerDefinition[]> {
        if (this._controllerDefinitions && this._controllerDefinitions.length !== 0) {
            return of(this._controllerDefinitions);
        }

        return this.controllerApiService.loadControllerTypes()
            .pipe(
                map(response => {
                    return this._controllerDefinitions = response.map(def => new ControllerDefinition(def));
                })
            );
    }

    public addController(locationId: number, addControllerModel: AddControllerModel): Observable<ToroIrrigationCrudResponseModel> {
        return this.controllerApiService.addController(locationId, addControllerModel);
    }

    public editController(controllerId: number, editControllerModel: EditControllerModel): Observable<ToroIrrigationCrudResponseModel> {
        return this.controllerApiService.editController(controllerId, editControllerModel);
    }

    public editStation(controllerId: number, stationNumber: number, station: ToroStationEdit): Observable<ToroStation> {
        return this.controllerApiService.editStation(controllerId, stationNumber, station);
    }

    public editAuxiliary(locationId: number, controllerId: number, auxiliaryNumber: number, auxiliaryEditModel: AuxiliaryEditModel): Observable<ToroStation> {
        return this.controllerApiService.editAuxiliary(controllerId, auxiliaryNumber, auxiliaryEditModel)
            .pipe(
                tap(() => {
                    const controllerToUpdate = this._controllerBundles[locationId].find(controller => controller.Controller.Id === controllerId);
                    const auxiliaryToUpdate = controllerToUpdate.Configuration.Auxiliaries[auxiliaryNumber - 1];

                    Auxiliary.updateAuxiliaryWithAuxiliaryEditModel(auxiliaryToUpdate, auxiliaryEditModel);
                })
            );
    }

    public replaceCommCard(controllerId: number, deviceId: string, controllerTypeId: number, pin?: number) {
        return this.controllerApiService.replaceCommCard(controllerId, deviceId, controllerTypeId, pin);
    }

    public sendRecordsToController(controllerId: number): Observable<FireAndWaitForNotificationCommandResponse> {
        return this.controllerApiService.sendRecordsToController(controllerId);
    }

    public getControllerRecords(controllerId: number, forceGetFromController = false): Observable<FireAndWaitForNotificationCommandResponse> {
        return this.controllerApiService.getControllerRecords(controllerId, forceGetFromController);
    }

    public getControllerConfigDisplayInfo(controllerId: number): Observable<ControllerInfoForConfigDisplay> {
        return this.controllerApiService.getControllerConfigDisplayInfo(controllerId);
    }

    public resyncCommunicationCard(controllerId: number): Observable<WaitUntilCompleteCommandResponse> {
        return this.controllerApiService.resyncCommunicationCard(controllerId);
    }

    public deleteIrrigationController(controllerId: number) {
        return this.controllerApiService.deleteIrrigationController(controllerId);
    }

    public exportConfiguration(controllerId: number): Observable<Blob> {
        return this.controllerApiService.exportConfiguration(controllerId);
    }

    public importConfiguration(controllerId: number, data: FormData, locationId: number): Observable<{ ControllerConfig: ToroControllerConfiguration, Success: boolean }> {
        return this.controllerApiService.importConfiguration(controllerId, data)
            .pipe(

                tap((response: { ControllerConfig: ToroControllerConfiguration, Success: boolean }) => {
                    const indexOfController = this._controllerBundles[locationId].findIndex(controller => controller.Controller.Id === controllerId);
                    this._controllerBundles[locationId][indexOfController].Configuration = new ToroControllerConfiguration(response.ControllerConfig);
                })
            );
    }

    public saveSingleProgram(controllerId: number, programNumber: number, description: string): Observable<{ Success: boolean, Error: string }> {
        return this.programApiService.saveProgram(controllerId, programNumber, description);
    }

    public saveSingleSchedule(controllerId: number, programNumber: number, schedule: ToroEditScheduleModel): Observable<{ Success: boolean, Error: string }> {
        return this.programApiService.saveSchedule(controllerId, programNumber, schedule);
    }

    public testAllStations(controllerId: number, durationInMinutes: number, maxStations: number, includeDiagnostics: boolean = false) {
        return this.stationApiService.testAllStations(controllerId, durationInMinutes, maxStations, includeDiagnostics);
    }

    public getControllerProgrammabilityUx(controllerId: number): Observable<ControllerProgrammability> {
        return this.controllerApiService.getControllerProgrammabilityUx(controllerId);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    public updateHelpUrl(isClimateLogicController)
    {
        return !isClimateLogicController
            ? `${Constants.imageBasePath}/SmartConnectTT.png`
            : `${Constants.imageBasePath}/ClimateLogicTT.png`;
    }

    public updateSmartConnectOrClimateLogic(context)
    {

        if (!context.isClimateLogicController)
        {
            context.eui = '';
            context.smartConnectOrClimateLogicMaxLength = 10;
            context.smartConnectOrClimateLogicMinLength = 1;
            context.smartConnectOrClimateLogicPattern = RegExp(/^[0-9]*$/);
        }
        else
        {
            context.eui = + context.climateLogicType === 1 ? '0024090201' : '0024090204';
            context.smartConnectOrClimateLogicMaxLength = 16;
            context.smartConnectOrClimateLogicMinLength = 16;
            context.smartConnectOrClimateLogicPattern = RegExp(/^[0-9a-fA-F]+$/);
        }
    }

    public hasNewFeatures(controllerBundle){
        let isLXC = controllerBundle.Controller.DeviceTypeId === Toro.DeviceType.LXCEXP;
        return (controllerBundle.Controller.IsControllerSupported && !controllerBundle.Controller.IsRecognized);
    }

}
